// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2024 Vic Zhu <viczhu2014@gmail.com>

import React from 'react'
import { useGetList, useGetOne, useStore, useTranslate } from 'react-admin'
import {
  buildGlucoseHoverText,
  buildGlucoseLineSegments,
  calculateAdaptiveGapThresholdMs,
  DEVICE_DATA_WINDOW_DAYS,
  displayGlucoseToMmol,
  formatGlucoseForDisplay,
  GLUCOSE_VALID_RANGE,
  getFixedGlucoseAxis,
  glucoseSegmentTraceShape,
  prepareGlucoseChartSeries,
} from '../common'
import { LazyPlot as Plot } from '../common/LazyPlot'
import { useFetchAllMetrics } from '../hooks/useFetchAllMetrics'
import { useGlucoseDisplay } from '../hooks/useGlucoseDisplay'
import { usePetTargetRange } from '../hooks/usePetTargetRange'
import { filterDeviceDataWithRanges } from '../utils/glucoseFiltering'

export interface GlucoseLineChartProps {
  deviceId?: string | number
  enableRangeSlider?: boolean
  /** Explicit band override (mmol/L). Omit to use the pet's resolved band. */
  minThreshold?: number
  maxThreshold?: number
}

export const GlucoseLineChart: React.FC<GlucoseLineChartProps> = React.memo(
  ({
    deviceId: propDeviceId,
    enableRangeSlider = false,
    minThreshold: propMinThreshold,
    maxThreshold: propMaxThreshold,
  }) => {
    const translate = useTranslate()
    const [storeDeviceId] = useStore('selectedDeviceId')
    const deviceId = propDeviceId ?? storeDeviceId
    const [selectedPetId] = useStore('selectedPetId')
    const { glucoseUnit, convertValue, formatValue } = useGlucoseDisplay()
    // The band drawn/colored on this chart is the PET'S band (per-pet override
    // → species default → dog default). This chart shipped for months with a
    // hardcoded HUMAN band (3.5–6.5 mmol/L), so every in-target dog reading
    // above 6.5 painted red on the Devices page.
    const { targetMin, targetMax } = usePetTargetRange(selectedPetId)
    const minThreshold = propMinThreshold ?? targetMin
    const maxThreshold = propMaxThreshold ?? targetMax

    // Threshold values in current unit
    const convertedMinThreshold = convertValue(minThreshold)
    const convertedMaxThreshold = convertValue(maxThreshold)

    // Fetch ALL metrics by paginating through all pages
    // PayloadCMS has a maxLimit (~250) that caps single requests
    const {
      data: rawMetricsData = [],
      isLoading: isLoadingMetrics,
      error: errorMetrics,
    } = useFetchAllMetrics({
      deviceId,
      sort: { field: 'timestamp', order: 'ASC' },
      enabled: !!deviceId,
    })

    // P60-4 / VP-PATIENTS-R44: this card used to bypass the single filter
    // path, charting pre-activation readings (a previous wear session —
    // possibly a previous PATIENT on a re-paired device) and warm-up values.
    // Same filter PetShow's charts get via useDeviceMetrics; lenient when
    // start_time is unknown, so a NULL-start_time device behaves as before.
    const { data: device } = useGetOne(
      'devices',
      { id: deviceId as number },
      { enabled: !!deviceId },
    )
    const metricsData = React.useMemo(
      () =>
        filterDeviceDataWithRanges(rawMetricsData, device?.start_time, device ? [device] : [])
          .filtered,
      // rawMetricsData is react-query's stable reference; start_time is a primitive.
      [rawMetricsData, device],
    )
    console.log('GlucoseLineChart: metrics data', metricsData.length)

    // Fetch all activities for the selected pet
    const activityFilter = selectedPetId ? { pet: selectedPetId } : {}

    const {
      data: events = [],
      isLoading: isLoadingEvents,
      error: errorEvents,
    } = useGetList('activities', {
      // Explicit depth (L-089): REST default 2 embeds every relation per row; only ids are read.
      meta: { params: { depth: 0 } },
      pagination: { page: 1, perPage: 10000 },
      sort: { field: 'start_time', order: 'ASC' },
      filter: activityFilter,
    })
    console.log('GlucoseLineChart: events data', events.length)

    // Apply defensive filtering: if data spans more than 2 weeks, keep only the most recent 2 weeks
    const getTwoWeeksAgoTimestamp = () => {
      if (metricsData.length === 0) {
        return Date.now() - DEVICE_DATA_WINDOW_DAYS * 24 * 60 * 60 * 1000
      }
      // Find the latest timestamp in the data
      const timestamps = metricsData.map((m) => m.timestamp * 1000)
      const latestTimestamp = Math.max(...timestamps)
      // Calculate 2 weeks before the latest data point
      return latestTimestamp - DEVICE_DATA_WINDOW_DAYS * 24 * 60 * 60 * 1000
    }

    const twoWeeksAgoTimestamp = getTwoWeeksAgoTimestamp()
    const defensivelyFilteredData = metricsData.filter((metric) => {
      const metricTimestamp = metric.timestamp * 1000
      return metricTimestamp >= twoWeeksAgoTimestamp
    })

    // Prepare chart data: missing values are gaps, numeric values are clipped to valid range.
    const filteredMetrics = prepareGlucoseChartSeries(defensivelyFilteredData)
    console.log('GlucoseLineChart: filtered metrics data', filteredMetrics.length)
    // Process data to include gaps for invalid readings
    const processedData = filteredMetrics
      .filter((m): m is typeof m & { timestamp: number } => m.timestamp !== undefined)
      .map((m) => ({
        timestamp: new Date(m.timestamp * 1000),
        glucose: convertValue(m.glucose),
      }))

    const metricsX = processedData.map((d) => d.timestamp)
    const metricsY = processedData.map((d) => d.glucose)
    const gapThresholdMs = calculateAdaptiveGapThresholdMs(metricsX)

    // Group metrics by hour for max/min calculations
    const hourlyStats = new Map<string, { max: number; min: number; hour: Date }>()
    filteredMetrics.forEach((m) => {
      if (m.timestamp !== undefined && typeof m.glucose === 'number' && !Number.isNaN(m.glucose)) {
        const date = new Date(m.timestamp * 1000)
        const hourKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}-${date.getHours()}`

        if (!hourlyStats.has(hourKey)) {
          hourlyStats.set(hourKey, {
            max: convertValue(m.glucose),
            min: convertValue(m.glucose),
            hour: new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours()),
          })
        } else {
          const stats = hourlyStats.get(hourKey)!
          stats.max = Math.max(stats.max, convertValue(m.glucose))
          stats.min = Math.min(stats.min, convertValue(m.glucose))
        }
      }
    })

    // Sort hourly stats by time
    const sortedHourlyStats = Array.from(hourlyStats.values()).sort(
      (a, b) => a.hour.getTime() - b.hour.getTime(),
    )

    const xMin = metricsX.length > 0 ? metricsX[0].getTime() : 0
    const xMax = metricsX.length > 0 ? metricsX[metricsX.length - 1].getTime() : 0

    const eventShapes = events
      .map((e) => {
        const x = new Date(e.start_time * 1000)
        if (x.getTime() < xMin || x.getTime() > xMax) return null
        return {
          type: 'line',
          x0: x,
          x1: x,
          y0: 0,
          y1: 1,
          xref: 'x',
          yref: 'paper',
          line: {
            color: 'orange',
            width: 2,
            dash: 'solid',
          },
          label: {
            text: `${e.type ?? ''} ${e.description ?? ''}`.trim().slice(0, 15),
            textposition: 'top center',
          },
        }
      })
      .filter(Boolean)

    // extract x positions and hover text for events
    const eventXs = events
      .map((e) => new Date(e.start_time * 1000))
      .filter((x) => x.getTime() >= xMin && x.getTime() <= xMax)
    const eventTexts = events
      .map((e) => `${e.type ?? ''} ${e.description ?? ''}`.trim())
      .filter((_, idx) => {
        const t = new Date(events[idx].start_time * 1000).getTime()
        return t >= xMin && t <= xMax
      })

    // Determine a y-value at the top of the glucose range for event markers
    const validGlucoseValues = metricsY.filter((y) => y !== null) as number[]
    const yMax = validGlucoseValues.length > 0 ? Math.max(...validGlucoseValues) * 1.02 : 0

    // Wait until metricsData is loaded and truly present
    if (isLoadingMetrics || isLoadingEvents) {
      return <div>{translate('pos.glucose.loading_chart')}</div>
    }
    if (errorMetrics || errorEvents) {
      return (
        <div>
          {translate('pos.glucose.error_loading_chart_detail', {
            error: errorMetrics?.message || errorEvents?.message || 'Unknown error',
          })}
        </div>
      )
    }

    // Build colored line segments via the shared helper: in-target black,
    // out-of-target red, and out-of-range (>30 / <2.2 mmol/L) grey-capped to the
    // axis boundary so highs ride the cap instead of spiking off-scale. Mirrors
    // the Glucose Trend chart so the two never disagree.
    const displayFloor = convertValue(GLUCOSE_VALID_RANGE.min)
    const displayCeil = getFixedGlucoseAxis(glucoseUnit).max
    const segments = buildGlucoseLineSegments(metricsX, metricsY as number[], {
      lowThreshold: convertedMinThreshold,
      highThreshold: convertedMaxThreshold,
      displayFloor,
      displayCeil,
      gapThresholdMs,
    })

    return (
      // Explicit wrapper height (matching layout.height) keeps Plotly's
      // useResizeHandler from measuring a collapsed parent on the first paint
      // and locking in a near-zero height until refresh (defect signature S6).
      <div
        data-testid="glucose-line-chart"
        style={{ width: '100%', maxWidth: '100vw', height: 250 }}
      >
        <Plot
          data={[
            // Add segments with different colors based on range
            ...segments.map((segment, idx) => ({
              x: segment.x,
              y: segment.y,
              type: 'scatter' as const,
              // A one-point 'lines' trace draws nothing — see
              // glucoseSegmentTraceShape. This is the card a vet opens from the
              // Devices page, so the newest reading vanishing here is the same
              // A0004BV defect that was fixed on the Glucose Trend chart.
              ...glucoseSegmentTraceShape(segment.x.length, segment.color),
              line: { color: segment.color, width: 2 },
              showlegend: false,
              text: buildGlucoseHoverText(
                segment.rawY,
                glucoseUnit,
                translate('pos.glucose.out_of_range'),
              ),
              hovertemplate: `%{x|%I:%M %p} | %{text}<extra></extra>`,
              name: `segment-${idx}`,
            })),
            {
              x: eventXs,
              y: Array(eventXs.length).fill(yMax), // place markers at top of chart
              mode: 'markers',
              marker: {
                symbol: 'line-ns-open',
                color: 'orange',
                size: 30,
              },
              hovertemplate: '%{text}<extra></extra>', // show event text on hover
              text: eventTexts,
              showlegend: false,
            },
          ]}
          layout={{
            title: '',
            xaxis: {
              title: translate('resources.glucose.axis_labels.time'),
              type: 'date',
              range: metricsX.length > 0 ? [metricsX[0], metricsX[metricsX.length - 1]] : undefined,
              rangeslider: { visible: enableRangeSlider },
              tickformat: '%a %I:%M %p',
              // Grow the margins to fit tick labels: at phone width the last
              // label ("Mon 12:00 AM") overhung the plot and was cut off at
              // the SVG edge. Layout only — no data or range change.
              automargin: true,
              gridcolor: '#e0e0e0',
              zerolinecolor: '#e0e0e0',
              showspikes: true,
              spikemode: 'across',
              spikethickness: 1,
              spikecolor: 'rgba(0, 0, 0, 0.3)',
              spikedash: 'dot',
              spikesnap: 'cursor',
            },
            yaxis: {
              title: `${translate('resources.glucose.axis_labels.glucose')} (${glucoseUnit})`,
              gridcolor: '#e0e0e0',
              zerolinecolor: '#e0e0e0',
              showspikes: true,
              spikemode: 'across',
              spikethickness: 1,
              spikecolor: 'rgba(0, 0, 0, 0.3)',
              spikedash: 'dot',
              spikesnap: 'cursor',
            },
            margin: { t: 10, l: 40, r: 10, b: 40 },
            plot_bgcolor: 'rgba(0,0,0,0)',
            paper_bgcolor: 'rgba(0,0,0,0)',
            font: {
              family: 'Roboto, sans-serif',
              size: 12,
              color: 'rgba(0, 0, 0, 0.87)',
            },
            height: 250,
            autosize: true,
            hovermode: 'x unified',
            hoverlabel: {
              bgcolor: '#000000',
              bordercolor: '#000000',
              font: {
                size: 14,
                color: '#FFFFFF',
                family: 'system-ui, -apple-system, sans-serif',
                weight: 600,
              },
              align: 'left',
              namelength: 0,
            },
            hoverdistance: 5,
            shapes: [
              // Light blue fill between thresholds
              {
                type: 'rect',
                x0: 0,
                x1: 1,
                y0: convertedMinThreshold,
                y1: convertedMaxThreshold,
                xref: 'paper',
                yref: 'y',
                fillcolor: 'rgba(173, 216, 230, 0.3)', // Light blue with transparency
                line: { width: 0 },
                layer: 'below',
              },
              // Threshold lines
              {
                type: 'line',
                x0: 0,
                x1: 1,
                y0: convertedMinThreshold,
                y1: convertedMinThreshold,
                yref: 'y',
                xref: 'paper',
                line: { color: 'gray', width: 1, dash: 'dash' },
              },
              {
                type: 'line',
                x0: 0,
                x1: 1,
                y0: convertedMaxThreshold,
                y1: convertedMaxThreshold,
                yref: 'y',
                xref: 'paper',
                line: { color: 'gray', width: 1, dash: 'dash' },
              },
              // Danger zone: low [2.2, 3.0]
              {
                type: 'rect',
                x0: 0,
                x1: 1,
                y0: convertValue(GLUCOSE_VALID_RANGE.min),
                y1: convertValue(3.0),
                xref: 'paper',
                yref: 'y',
                fillcolor: 'rgba(99, 102, 241, 0.15)', // light purple
                line: { width: 0 },
                layer: 'below',
              },
              // Danger zone: high [30, 40]
              {
                type: 'rect',
                x0: 0,
                x1: 1,
                y0: convertValue(30),
                y1: convertValue(GLUCOSE_VALID_RANGE.max),
                xref: 'paper',
                yref: 'y',
                fillcolor: 'rgba(239, 68, 68, 0.15)', // light red
                line: { width: 0 },
                layer: 'below',
              },
              // @ts-expect-error
              ...eventShapes,
            ],
          }}
          useResizeHandler
          style={{ width: '100%', height: '100%' }}
          config={{
            displayModeBar: false,
            responsive: true,
            showTips: false,
            doubleClick: false,
            modeBarButtonsToRemove: [
              'pan2d',
              'select2d',
              'lasso2d',
              'resetScale2d',
              'zoomIn2d',
              'zoomOut2d',
            ],
          }}
        />
        {/* Display hourly max/min values below the chart */}
        <div
          style={{
            marginTop: '10px',
            fontFamily: 'monospace',
            fontSize: '12px',
            overflowX: 'auto',
            whiteSpace: 'nowrap',
            backgroundColor: '#f5f5f5',
            padding: '10px',
            borderRadius: '4px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start' }}>
            <div
              style={{
                minWidth: '40px',
                marginRight: '10px',
                fontWeight: 'bold',
              }}
            >
              <div>{translate('resources.glucose.axis_labels.max')}:</div>
              <div>{translate('resources.glucose.axis_labels.min')}:</div>
            </div>
            <div style={{ display: 'flex', gap: '15px' }}>
              {sortedHourlyStats.map((stats, idx) => {
                // stats are in the display unit; convert back to mmol/L for the
                // shared formatter so out-of-range peaks/nadirs show >30 / <2.2.
                // displayGlucoseToMmol, not a bare /18.0182: integer mg/dL
                // rounding makes 2.2 mmol read back as 2.21998, defeating the
                // <=2.2 mask — the drop-out then printed a confident "40".
                const toMmol = (v: number) => displayGlucoseToMmol(v, glucoseUnit)
                return (
                  <div key={idx} style={{ textAlign: 'center', minWidth: '35px' }}>
                    <div>
                      {typeof stats.max === 'number' && !Number.isNaN(stats.max)
                        ? formatGlucoseForDisplay(toMmol(stats.max), glucoseUnit, {
                            withUnit: false,
                          })
                        : '-'}
                    </div>
                    <div>
                      {typeof stats.min === 'number' && !Number.isNaN(stats.min)
                        ? formatGlucoseForDisplay(toMmol(stats.min), glucoseUnit, {
                            withUnit: false,
                          })
                        : '-'}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    )
  },
)
